import { DrawerItem } from '@progress/kendo-angular-layout';
import {
  SVGIcon,
  chartColumnStackedIcon,
  chartPieIcon,
  documentManagerIcon,
  envelopIcon,
  fileWrenchIcon,
  gearsIcon,
  gridLayoutIcon,
  homeIcon,
  hyperlinkOpenIcon,
  searchIcon,
  securityCheckOutlineIcon,
  starIcon,
  trackChangesIcon,
  userIcon,
  warningTriangleIcon,
} from '@progress/kendo-svg-icons';

export interface ExtendedDrawerItem extends DrawerItem {
  svgIcon?: SVGIcon;
  path?: string;
  selected?: boolean;
  external?: boolean;
  cssClass?: string;
}

export interface NavUserContext {
  primaryOrgId?: number | null;
  primaryOrgAccountType?: number | null;
  groups: string[];
  /** Holds the Partner Portal grant, not just an MSP or MMSP org. */
  partnerPortal?: boolean;
}

function isPartnerOrg(user: NavUserContext): boolean {
  return user.primaryOrgAccountType === 2 || user.primaryOrgAccountType === 3;
}

/** Helper — check a flag from the raw flags record. */
function flag(flags: Record<string, boolean>, name: string): boolean {
  return flags[name] === true;
}

export function buildNavItems(user: NavUserContext, flags: Record<string, boolean> = {}): ExtendedDrawerItem[] {
  return [
    { id: 0, text: 'Dashboard', svgIcon: homeIcon, path: '/customer/customer-dashboard', external: false },

    // Cases: cases_v2 → new Cases UI; otherwise legacy external link + Case Log
    ...(flag(flags, 'cases_v2')
      ? [{ id: 1, text: 'Cases', svgIcon: trackChangesIcon, path: '/customer/cases', external: false }]
      : [
          {
            id: 1,
            text: 'Cases',
            svgIcon: hyperlinkOpenIcon,
            path: 'https://desk.cyflare.cloud/portal/',
            external: true,
          },
          { id: 2, text: 'Case Log', svgIcon: trackChangesIcon, path: '/customer/tickets-log', external: false },
        ]),

    { id: 3, text: 'Analytics', svgIcon: chartPieIcon, path: '/customer/analytics', external: false },

    // Identity Security: only shown when itdr flag is enabled
    ...(flag(flags, 'itdr')
      ? [
          {
            id: 14,
            text: 'Identity Security',
            svgIcon: securityCheckOutlineIcon,
            path: '/customer/itdr',
            external: false,
          },
        ]
      : []),

    // Asset Intelligence: only shown when invetory flag is enabled
    ...(flag(flags, 'inventory')
      ? [
          {
            id: 15,
            text: 'Asset Intelligence',
            svgIcon: gridLayoutIcon,
            path: '/customer/asset-intelligence',
            external: false,
          },
        ]
      : []),

    // Data House: only shown when data_house flag is enabled
    ...(flag(flags, 'data_house')
      ? [
          {
            id: 16,
            text: 'Data House',
            svgIcon: searchIcon,
            path: '/customer/data-house',
            external: false,
          },
        ]
      : []),

    { id: 4, text: 'SOC Comms', svgIcon: envelopIcon, path: '/customer/customer-soc-comms', external: false },
    { id: 5, text: 'SOC Tuning', svgIcon: fileWrenchIcon, path: '/customer/soc-tuning', external: false },
    {
      id: 6,
      text: 'Alert Preferences',
      svgIcon: warningTriangleIcon,
      path: '/customer/alert-preferences',
      external: false,
    },
    { id: 7, text: 'Reporting', svgIcon: documentManagerIcon, path: '/customer/reporting-v2', external: false },

    // Policy Analysis: only shown when policy_analysis flag is true
    ...(flag(flags, 'policy_analysis')
      ? [
          {
            id: 13,
            text: 'Policy Analysis',
            svgIcon: searchIcon,
            path: '/customer/policy-gap-analysis',
            external: false,
          },
        ]
      : []),
    { id: 8, text: 'Integrations', svgIcon: gearsIcon, path: '/customer/integrations', external: false },
    // CLIENT MANAGEMENT: MSP or MMSP orgs, plus anyone granted the Partner Portal (Cyflare admins included)
    ...(isPartnerOrg(user) || user.partnerPortal
      ? [
          { id: 9, separator: true },
          { id: 10, text: 'CLIENT MANAGEMENT', svgIcon: undefined, disabled: true, cssClass: 'drawer-section-label' },
        ]
      : []),
    ...(isPartnerOrg(user)
      ? [
          {
            id: 11,
            text: 'Service Intelligence',
            svgIcon: chartColumnStackedIcon,
            path: '/customer/msp-portal/partner-analytics',
            external: false,
          },
        ]
      : []),
    // Granted users only: being at a partner org isn't enough.
    ...(user.partnerPortal
      ? [{ id: 17, text: 'Partner Portal', svgIcon: starIcon, path: '/partner/home', external: false }]
      : []),
    // Show Customers tab for Cyflare admins (org ID 1)
    ...(user.primaryOrgId === 1 && user.groups.includes('admin')
      ? [{ id: 12, text: 'Customers', svgIcon: userIcon, path: '/customer/msp-portal/customers', external: false }]
      : []),
  ];
}

/** Routes that require a specific feature flag. Used by the layout effect to redirect on org switch. */
export const FLAG_GATED_ROUTES: ReadonlyArray<{ flag: string; pathPrefix: string }> = [
  { flag: 'cases_v2', pathPrefix: '/customer/cases' },
  { flag: 'itdr', pathPrefix: '/customer/itdr' },
  { flag: 'policy_analysis', pathPrefix: '/customer/policy-gap-analysis' },
  { flag: 'inventory', pathPrefix: '/customer/asset-intelligence' },
  { flag: 'data_house', pathPrefix: '/customer/data-house' },
];

export const INFO_NAV: Array<{ text: string; url: string; external: boolean }> = [
  { text: 'SOC Change Log', url: 'https://cyflare.com/soc-change-log/', external: true },
  { text: 'Detections Board', url: 'https://detections.cyflare.com/', external: true },
  { text: 'Satisfaction Survey', url: 'https://survey.zohopublic.com/zs/QRRLP1', external: true },
];
